// Inline-SVG icon set ported from cardrive/admin-shell.jsx. Backoffice
// uses its own narrow icon vocabulary (sidebar nav + chrome). Distinct
// from packages/ui/NamedIcon which serves the public site's CMS blocks.

import type { ReactElement } from 'react';

const ICONS = {
  home: <path d="M3 12L12 4l9 8M5 10v10h14V10" />,
  cal: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 9h18M8 3v4M16 3v4" />
    </>
  ),
  car: (
    <path d="M3 13l2-6h14l2 6M5 13h14v5H5zM7 18v2M17 18v2M7 13a1 1 0 100-2 1 1 0 000 2zm10 0a1 1 0 100-2 1 1 0 000 2z" />
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="3.2" />
      <circle cx="17" cy="10" r="2.6" />
      <path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6M14 20c0-2.3 1.5-4.5 4-4.5s3 1.5 3 4.5" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8" r="3.4" />
      <path d="M5 20c0-3.5 3-6.4 7-6.4s7 2.9 7 6.4" />
    </>
  ),
  star: <path d="M12 3l2.95 6.36 6.95.65-5.25 4.64L18.2 21 12 17.55 5.8 21l1.55-6.35L2.1 9.99l6.95-.63z" />,
  shield: <path d="M12 3l8 3v6c0 5-3.5 8.5-8 9-4.5-.5-8-4-8-9V6l8-3z" />,
  msg: <path d="M4 6a2 2 0 012-2h12a2 2 0 012 2v9a2 2 0 01-2 2h-7l-5 4v-4H6a2 2 0 01-2-2V6z" />,
  tag: (
    <>
      <path d="M4 13l9-9h7v7l-9 9-7-7z" />
      <circle cx="16" cy="8" r="1.4" />
    </>
  ),
  umbrella: (
    <>
      <path d="M12 3v3M3 12a9 9 0 0118 0c-2 0-3 1-4.5 1S14 12 12 12s-3 1-4.5 1S5 12 3 12z" />
      <path d="M12 12v6a3 3 0 003 3" />
    </>
  ),
  bank: <path d="M3 9l9-5 9 5v2H3V9zM5 11v8h2v-8M11 11v8h2v-8M17 11v8h2v-8M3 19h18v2H3z" />,
  graph: <path d="M4 18l5-7 4 3 6-9M4 21h16" />,
  chart: <path d="M4 21V8M10 21V4M16 21v-9M22 21H2" />,
  gear: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 1v3M12 20v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M1 12h3M20 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.3-4.3" />
    </>
  ),
  bell: <path d="M6 16V11a6 6 0 1112 0v5l1.5 2H4.5L6 16zM10 21h4" />,
  plus: <path d="M12 5v14M5 12h14" />,
  arrowR: <path d="M5 12h14M13 6l6 6-6 6" />,
  flag: <path d="M5 21V4h13l-2 4 2 4H5" />,
  globe: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3a14 14 0 010 18M12 3a14 14 0 000 18" />
    </>
  ),
  chev: <path d="M9 6l6 6-6 6" />,
  download: <path d="M12 4v12M6 12l6 6 6-6M4 20h16" />,
  filter: <path d="M3 5h18l-7 9v6l-4-2v-4L3 5z" />,
  dot: <circle cx="12" cy="12" r="3" />,
  check: <path d="M5 12l4 4 10-10" />,
  x: <path d="M6 6l12 12M18 6L6 18" />,
} satisfies Record<string, ReactElement>;

export type AdminIconName = keyof typeof ICONS;

export interface AdminIconProps {
  name: AdminIconName;
  className?: string;
}

export function AdminIcon({ name, className = 'h-4 w-4' }: AdminIconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {ICONS[name]}
    </svg>
  );
}
